import { Button, Input } from "@mantine/core";
import { Checkbox, Table, notification } from "antd";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { CSS } from "@dnd-kit/utilities";
import type { DragEndEvent } from "@dnd-kit/core";
import { IconArrowLeft } from "@tabler/icons-react";
import { orpcClient } from "@/lib/orpc-client";
import { single, allOrThrow } from "@/lib/orpc-batch";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface DraggableHeaderCellProps
  extends React.HTMLAttributes<HTMLTableCellElement> {
  id?: string;
}

const DraggableHeaderCell: React.FC<DraggableHeaderCellProps> = ({
  id,
  ...restProps
}) =>
  id ? <SortableHeaderCell id={id} {...restProps} /> : <th {...restProps} />;

const SortableHeaderCell: React.FC<
  DraggableHeaderCellProps & { id: string }
> = ({
  id,
  children,
  ...restProps
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    ...restProps.style,
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: "move",
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <th
      {...restProps}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    >
      {children}
    </th>
  );
};

function DesignBOM() {
  const { t } = useTranslation();
  const [fileUpload, setFileUpload] = useState([]);
  const [designBOM, setDesignBOM] = useState([]);
  const [editRowId, setEditRowId] = useState(null);
  const [editRowData, setEditRowData] = useState([]);
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  const [filters, setFilters] = useState({
    guandaId: "",
    installationLocation: "",
    metricSizeMark: "",
    transportNumber: "",
  });
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [deleteFileUpload, setDeleteFileUpload] = useState([]);
  const [api, contextHolder] = notification.useNotification();

  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const fileUploadRef = useRef(fileUpload);

  const COLUMN_ORDER_STORAGE_KEY = `steelAssemblyBOM_columnOrder_${id}`;
  const COLUMN_VISIBILITY_STORAGE_KEY = `steelAssemblyBOM_columnVisibility_${id}`;

  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    const saved = localStorage.getItem(COLUMN_ORDER_STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  });

  const [hiddenColumns, setHiddenColumns] = useState<string[]>(() => {
    const saved = localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    })
  );

  const getFiltersFromURL = () => {
    return {
      guandaId: searchParams.get("guandaId") || "",
      installationLocation: searchParams.get("installationLocation") || "",
      metricSizeMark: searchParams.get("metricSizeMark") || "",
      transportNumber: searchParams.get("transportNumber") || "",
    };
  };

  const getCurrentPageFromURL = () => {
    const page = searchParams.get("page");
    return page ? parseInt(page, 10) : 1;
  };

  const getPageSizeFromURL = () => {
    const size = searchParams.get("pageSize");
    return size ? parseInt(size, 10) : 25;
  };

  const updateURL = (newFilters?: any, page?: number, size?: number) => {
    const params = new URLSearchParams(searchParams);

    if (newFilters !== undefined) {
      Object.entries(newFilters).forEach(([key, value]) => {
        if (value && value !== "") {
          params.set(key, value as string);
        } else {
          params.delete(key);
        }
      });
    }

    if (page !== undefined) {
      if (page > 1) {
        params.set("page", page.toString());
      } else {
        params.delete("page");
      }
    }

    if (size !== undefined && size !== 25) {
      params.set("pageSize", size.toString());
    } else if (size === 25) {
      params.delete("pageSize");
    }

    setSearchParams(params);
  };

  const handleSelectionChange = useCallback(
    (selectedKeys: any, selectedRows: any) => {
      setSelectedRowKeys(selectedKeys);

      setDeleteFileUpload((prevDeleteFileUpload: any) => {
        const currentPageIds = fileUploadRef.current.map((item) => item.id);
        const otherPagesSelected = prevDeleteFileUpload.filter(
          (item) => !currentPageIds.includes(item.id)
        );

        const currentPageSelected = fileUploadRef.current.filter((item) =>
          selectedKeys.includes(item.id)
        );

        return [...otherPagesSelected, ...currentPageSelected];
      });
    },
    []
  );

  const rowSelection = useMemo(
    () => ({
      type: "checkbox" as const,
      fixed: "left" as const,
      selectedRowKeys: selectedRowKeys,
      preserveSelectedRowKeys: true,
      onChange: handleSelectionChange,
      getCheckboxProps: (record: any) => ({
        disabled: record.name === "Disabled User",
        name: record.name,
      }),
    }),
    [selectedRowKeys, handleSelectionChange]
  );

  const handleChangePayableDetail = (documentId, value, type, i) => {
    setEditRowData((prev) => ({
      ...prev,

      // steelAssembly: {
      //   ...prev?.steelAssembly,
      //   [type]: value?.trim(),
      // },
      [type]: value?.trim(),
    }));
  };

  const rowEditDisplay = (component, i, input) => {
    return editRowId === i?.id ? component : <div>{input}</div>;
  };

  // const handleChangeCSV = (info, setFileUpload) => {
  //   const file = info.file.originFileObj;
  //   if (file && file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
  //     const reader = new FileReader();

  //     reader.onload = (e) => {
  //       const data = new Uint8Array(e.target.result);
  //       const workbook = XLSX.read(data, { type: 'array' });
  //       const sheetName = workbook.SheetNames[0]; // Read the first sheet
  //       const worksheet = workbook.Sheets[sheetName];
  //       const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' }); // Convert to JSON

  //       const filteredData = jsonData.filter((item) => {
  //         let part = Object.values(item)[0];
  //         return part === 'SteelAssembly';
  //       });

  //       const formattedData = filteredData.map((item, index) => ({
  //         key: index,
  //         name: item?.新建工程 || '',
  //         b: item?.__EMPTY || '',
  //         c: item?.__EMPTY_1 || '',
  //         d: item?.__EMPTY_2 || '',
  //         e: item?.__EMPTY_3 || '',
  //         f: item?.__EMPTY_4 || '',
  //         g: item?.__EMPTY_5 || '',
  //         h: item?.__EMPTY_6 || '',
  //         i: item?.__EMPTY_7 || '',
  //         j: item?.__EMPTY_8 || '',
  //         k: item?.__EMPTY_9 || '',
  //         l: item?.__EMPTY_10 || '',
  //         m: item?.__EMPTY_11 || '',
  //         n: item?.__EMPTY_12 || '',
  //       }));

  //       setFileUpload(formattedData);
  //     };

  //     reader.readAsArrayBuffer(file); // Read file as binary buffer
  //   } else {
  //     setFileUpload((prev) => [...prev, { id: Date.now(), name: file.name }]);
  //   }
  // };

  const baseColumns = [
    {
      key: "steelAssemblyId",
      title: t("steelAssemblyBOM.componentId") || "構件ID",
      dataIndex: "steelAssemblyId",
      hidden: hiddenColumns.includes("steelAssemblyId"),

      render: (text, i) => {
        console.log(i);
        console.log("editRowData", editRowData);
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={editRowData?.steelAssemblyId?.trim()}
            // disabled={editRowId !== i?.id}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "steelAssemblyId",
                i
              )
            }
          />,
          i,
          i?.steelAssemblyId
        );
      },
    },
    {
      key: "guandaId",
      title: t("steelAssemblyBOM.guandaId") || "廣達ID",
      dataIndex: "guandaId",
      hidden: hiddenColumns.includes("guandaId"),

      render: (text, i) => {
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            value={editRowData?.guandaId?.toString()?.trim() || ""}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "guandaId",
                i
              )
            }
          />,
          i,
          i?.guandaId?.toString().padStart(7, "0")
        );
      },
    },
    {
      key: "steelAssemblyCode",
      title: t("steelAssemblyBOM.componentCode") || "構件編號",
      dataIndex: "steelAssemblyCode",
      hidden: hiddenColumns.includes("steelAssemblyCode"),

      render: (text, i) => {
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={editRowData?.steelAssemblyCode?.trim()}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "steelAssemblyCode",
                i
              )
            }
          />,
          i,
          i?.steelAssemblyCode
        );
      },
    },
    {
      key: "installationLocation",
      title: t("steelAssemblyBOM.installationLocation") || "安裝位置",
      dataIndex: "installationLocation",
      hidden: hiddenColumns.includes("installationLocation"),
      render: (text, i) => {
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={editRowData?.installationLocation}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "installationLocation",
                i
              )
            }
          />,
          i,
          i?.installationLocation
        );
      },
    },
    {
      key: "installationElevation",
      title: t("steelAssemblyBOM.installationElevation") || "安裝高程",
      dataIndex: "installationElevation",
      hidden: hiddenColumns.includes("installationElevation"),
      render: (text, i) => {
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={editRowData?.installationElevation}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "installationElevation",
                i
              )
            }
          />,
          i,
          i?.installationElevation
        );
      },
    },
    {
      key: "distinction",
      title: t("steelAssemblyBOM.distinction") || "區別",
      dataIndex: "distinction",
      hidden: hiddenColumns.includes("distinction"),
      render: (text, i) => {
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={editRowData?.distinction}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "distinction",
                i
              )
            }
          />,
          i,
          i?.distinction
        );
      },
    },
    {
      key: "transportNumber",
      title: t("steelAssemblyBOM.transportNumber") || "拆運號碼",
      dataIndex: "transportNumber",
      hidden: hiddenColumns.includes("transportNumber"),
      render: (text, i) => {
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={editRowData?.transportNumber}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "transportNumber",
                i
              )
            }
          />,
          i,
          i?.transportNumber
        );
      },
    },
    {
      key: "transportDescription",
      title: t("steelAssemblyBOM.transportDescription") || "拆運說明",
      dataIndex: "transportDescription",
      hidden: hiddenColumns.includes("transportDescription"),
      render: (text, i) => {
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={editRowData?.transportDescription}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "transportDescription",
                i
              )
            }
          />,
          i,
          i?.transportDescription
        );
      },
    },
    {
      key: "drawingName",
      title: t("steelAssemblyBOM.drawingName") || "圖面名稱",
      dataIndex: "drawingName",
      hidden: hiddenColumns.includes("drawingName"),
      render: (text, i) => {
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={editRowData?.drawingName}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "drawingName",
                i
              )
            }
          />,
          i,
          i?.drawingName
        );
      },
    },
    {
      key: "totalWidth",
      title: t("steelAssemblyBOM.totalWidth") || "總寬度",
      dataIndex: "totalWidth",
      hidden: hiddenColumns.includes("totalWidth"),
      render: (text, i) => {
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={editRowData?.totalWidth}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "totalWidth",
                i
              )
            }
          />,
          i,
          i?.totalWidth
        );
      },
    },
    {
      key: "totalHeight",
      title: t("steelAssemblyBOM.totalHeight") || "總高度",
      dataIndex: "totalHeight",
      hidden: hiddenColumns.includes("totalHeight"),
      render: (text, i) => {
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={editRowData?.totalHeight}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "totalHeight",
                i
              )
            }
          />,
          i,
          i?.totalHeight
        );
      },
    },
    {
      key: "totalLength",
      title: t("steelAssemblyBOM.totalLength") || "總長度",
      dataIndex: "totalLength",
      hidden: hiddenColumns.includes("totalLength"),
      render: (text, i) => {
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={editRowData?.totalLength}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "totalLength",
                i
              )
            }
          />,
          i,
          i?.totalLength
        );
      },
    },
    {
      key: "totalWeight",
      title: t("steelAssemblyBOM.totalWeight") || "總重量",
      dataIndex: "totalWeight",
      hidden: hiddenColumns.includes("totalWeight"),
      render: (text, i) => {
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={editRowData?.totalWeight}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "totalWeight",
                i
              )
            }
          />,
          i,
          i?.totalWeight
        );
      },
    },
    {
      key: "totalArea",
      title: t("steelAssemblyBOM.totalArea") || "總面積",
      dataIndex: "totalArea",
      hidden: hiddenColumns.includes("totalArea"),
      render: (text, i) => {
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={editRowData?.totalArea}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "totalArea",
                i
              )
            }
          />,
          i,
          i?.totalArea
        );
      },
    },
    {
      key: "metricSizeMark",
      title: t("steelAssemblyBOM.sectionSpecification") || "斷面規格",
      dataIndex: "metricSizeMark",
      hidden: hiddenColumns.includes("metricSizeMark"),
      render: (text, i) => {
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={editRowData?.metricSizeMark?.trim()}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "metricSizeMark",
                i
              )
            }
          />,
          i,
          i?.metricSizeMark
        );
      },
    },
    {
      key: "actions",
      title: "",
      fixed: "right",
      hidden: hiddenColumns.includes("actions"),
      render: (text, i) => {
        return (
          <>
            {editRowId ? (
              <>
                <Button
                  variant="default"
                  style={{ marginRight: "15px" }}
                  onClick={() => {
                    setEditRowId(null);
                    setEditRowData([]);
                  }}
                >
                  {t("steelAssemblyBOM.cancel") || "取消"}
                </Button>
                <Button
                  variant="default"
                  onClick={async (element) => {
                    try {
                      if (!id) return;

                      const result = await single(
                        orpcClient.materialBom.update([
                          {
                            id: editRowData?.id,
                            projectId: id,
                            steelAssemblyId: String(
                              editRowData?.steelAssemblyId?.trim()
                            ),
                            // guandaId 不是 materialBom 的欄位，server schema 會拒收
                            // guandaId: editRowData?.guandaId,
                            steelAssemblyCode:
                              editRowData?.steelAssemblyCode?.trim(),
                            installationLocation:
                              editRowData?.installationLocation?.trim(),
                            installationElevation:
                              editRowData?.installationElevation,
                            distinction: String(
                              editRowData?.distinction?.trim()
                            ),
                            transportNumber:
                              editRowData?.transportNumber?.trim(),
                            transportDescription:
                              editRowData?.transportDescription?.trim(),
                            drawingName: editRowData?.drawingName?.trim(),
                            totalWidth: editRowData?.totalWidth,
                            totalHeight: editRowData?.totalHeight,
                            totalLength: editRowData?.totalLength,
                            totalWeight: editRowData?.totalWeight,
                            totalArea: editRowData?.totalArea,
                            metricSizeMark:
                              editRowData?.metricSizeMark?.trim(),
                          },
                        ])
                      );

                      if (result) {
                        api.success({
                          message: `Notification `,
                          description: (
                            <div>
                              {t("steelAssemblyBOM.editSuccess") || "編輯成功"}
                            </div>
                          ),
                          placement: "topLeft",
                          duration: 0,
                        });
                      }
                    } catch (error) {
                      api.error({
                        message: `Notification `,
                        description: (
                          <div>
                            {t("steelAssemblyBOM.editFailed") || "編輯失敗"}
                          </div>
                        ),
                        placement: "topLeft",
                        duration: 0,
                      });
                    }

                    fetchAllData();
                    setEditRowId(null);
                    setEditRowData([]);
                  }}
                >
                  {t("steelAssemblyBOM.save") || "儲存"}
                </Button>
              </>
            ) : (
              <Button
                variant="default"
                style={{ marginRight: "15px" }}
                onClick={() => {
                  setEditRowId(i?.id);
                  setEditRowData(i);
                }}
              >
                {t("steelAssemblyBOM.edit") || "編輯"}
              </Button>
            )}
          </>
        );
      },
    },
  ];

  const fetchAllData = async () => {
    try {
      if (!id) return;

      const result = await orpcClient.materialBom.list({
        filter: { projectId: id },
      });

      setAllData(result);
      setDesignBOM({
        meta: {
          pagination: {
            total: result.length,
            page: 1,
            pageSize: result.length,
            pageCount: 1,
          },
        },
      } as any);
    } catch (error) {
      console.error("Error fetching department data:", error);
    }
  };

  const fetchData = async (page = 1, currentPageSize = pageSize) => {
    // Client-side pagination of filtered data
    const startIndex = (page - 1) * currentPageSize;
    const endIndex = startIndex + currentPageSize;
    const paginatedData = filteredData.slice(startIndex, endIndex);
    setFileUpload(paginatedData);
  };

  const handleDeleteFileUpload = async (deleteFileUpload) => {
    if (!deleteFileUpload?.length) {
      return api.error({
        message: `Notification `,
        description: (
          <div>
            {t("steelAssemblyBOM.noItemsToDelete") || "請勾選需要刪除的項目"}
          </div>
        ),
        placement: "topLeft",
        duration: 0,
      });
    }
    if (!id) return;

    try {
      await allOrThrow(
        orpcClient.materialBom.delete(
          deleteFileUpload.map((element) => ({ id: element?.id }))
        )
      );

      setSelectedRowKeys([]);
      setDeleteFileUpload([]);
      fetchAllData();
      api.success({
        message: `Notification `,
        description: (
          <div>{t("steelAssemblyBOM.deleteSuccess") || "刪除成功"}</div>
        ),
        placement: "topLeft",
        duration: 0,
      });
    } catch (e) {
      console.warn("error", e);
      return e;
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    const newFilters = {
      ...filters,
      [field]: value,
    };
    setFilters(newFilters);
    setCurrentPage(1);
    updateURL(newFilters, 1);

    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new timeout for filtering
    debounceTimeoutRef.current = setTimeout(() => {
      applyFiltersWithData(newFilters);
    }, 500); // 500ms delay
  };

  const applyFiltersWithData = (filtersToApply = filters) => {
    const filtered = allData.filter((item) => {
      const guandaIdMatch =
        !filtersToApply.guandaId ||
        item?.steelAssembly?.guandaId
          ?.toString()
          .includes(filtersToApply.guandaId);

      const installationLocationMatch =
        !filtersToApply.installationLocation ||
        item?.steelAssembly?.installationLocation
          ?.toLowerCase()
          .includes(filtersToApply.installationLocation.toLowerCase());

      const metricSizeMarkMatch =
        !filtersToApply.metricSizeMark ||
        item?.steelAssembly?.metricSizeMark
          ?.toLowerCase()
          .includes(filtersToApply.metricSizeMark.toLowerCase());

      const transportNumberMatch =
        !filtersToApply.transportNumber ||
        item?.steelAssembly?.transportNumber
          ?.toString()
          .includes(filtersToApply.transportNumber);

      return (
        guandaIdMatch &&
        installationLocationMatch &&
        metricSizeMarkMatch &&
        transportNumberMatch
      );
    });

    setFilteredData(filtered);
    setCurrentPage(1);

    // Show filtered data with pagination
    const paginatedData = filtered.slice(0, pageSize);
    setFileUpload(paginatedData);
  };

  const clearFilters = () => {
    const clearedFilters = {
      guandaId: "",
      installationLocation: "",
      metricSizeMark: "",
      transportNumber: "",
    };
    setFilters(clearedFilters);
    setFilteredData(allData);
    setCurrentPage(1);
    updateURL(clearedFilters, 1);

    // Show all data with pagination
    const paginatedData = allData.slice(0, pageSize);
    setFileUpload(paginatedData);
  };

  const applyFilters = () => {
    setCurrentPage(1);
    updateURL(filters, 1);
    applyFiltersWithData(filters);
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      const activeIndex = columnOrder.indexOf(active.id as string);
      const overIndex = columnOrder.indexOf(over?.id as string);
      const newColumnOrder = arrayMove(columnOrder, activeIndex, overIndex);
      setColumnOrder(newColumnOrder);
      localStorage.setItem(
        COLUMN_ORDER_STORAGE_KEY,
        JSON.stringify(newColumnOrder)
      );
    }
  };

  const columns = columnOrder
    .map((key) => baseColumns.find((col) => col.key === key))
    .filter((col): col is (typeof baseColumns)[number] => col !== undefined)
    .map((col, index) => {
      const newCol = { ...col };
      if (index === 0) {
        newCol.fixed = "left" as const;
      } else {
        delete newCol.fixed;
      }
      return newCol;
    });

  useEffect(() => {
    if (columnOrder.length === 0) {
      const defaultOrder = baseColumns.map((col) => col.key);
      setColumnOrder(defaultOrder);
      localStorage.setItem(
        COLUMN_ORDER_STORAGE_KEY,
        JSON.stringify(defaultOrder)
      );
    }
  }, []);

  useEffect(() => {
    const urlFilters = getFiltersFromURL();
    const urlPage = getCurrentPageFromURL();
    const urlPageSize = getPageSizeFromURL();

    setFilters(urlFilters);
    setCurrentPage(urlPage);
    setPageSize(urlPageSize);

    fetchAllData();
  }, [id]);

  useEffect(() => {
    const urlFilters = getFiltersFromURL();
    const urlPage = getCurrentPageFromURL();
    const urlPageSize = getPageSizeFromURL();

    if (
      JSON.stringify(urlFilters) !== JSON.stringify(filters) ||
      urlPage !== currentPage ||
      urlPageSize !== pageSize
    ) {
      setFilters(urlFilters);
      setCurrentPage(urlPage);
      setPageSize(urlPageSize);

      // Apply filters after allData is loaded
      if (allData.length > 0) {
        applyFiltersWithData(urlFilters);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    // When allData changes, set initial filtered data
    setFilteredData(allData);
    setCurrentPage(1);
    fetchData(1, pageSize);
  }, [allData]);

  useEffect(() => {
    // When filteredData or pagination changes, update display
    fetchData(currentPage, pageSize);
  }, [filteredData, currentPage, pageSize]);

  useEffect(() => {
    fileUploadRef.current = fileUpload;
  }, [fileUpload]);

  useEffect(() => {
    const handlePaginationInput = () => {
      const paginationInputs = document.querySelectorAll(
        ".ant-pagination-options .ant-select input"
      );
      paginationInputs.forEach((input) => {
        const inputElement = input as HTMLInputElement;
        inputElement.readOnly = true;
        inputElement.style.cursor = "pointer";
        inputElement.style.pointerEvents = "none";
        inputElement.style.caretColor = "transparent";
        inputElement.style.userSelect = "none";
      });

      const selectors = document.querySelectorAll(
        ".ant-pagination-options .ant-select .ant-select-selector"
      );
      selectors.forEach((selector) => {
        (selector as HTMLElement).style.cursor = "pointer";
      });
    };

    const observer = new MutationObserver(handlePaginationInput);
    observer.observe(document.body, { childList: true, subtree: true });

    const timeoutId = setTimeout(handlePaginationInput, 100);

    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
      // Clean up debounce timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="px-8 m-8">
      {contextHolder}
      <div
        className="flex items-center justify-between px-4"
        style={{ backgroundColor: "#ECECEC", marginBottom: "1rem" }}
      >
        <Button
          //onClick={() => navigate(`/hrMainPage/addSteelAssemblyBOM/${id}`)}
          variant="filled"
          color="red"
          onClick={() => handleDeleteFileUpload(deleteFileUpload)}
        >
          {t("steelAssemblyBOM.deleteSelected") || "刪除勾選的項目"}
        </Button>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            color="gray"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate(`/hrMainPage/projectQuotation/${id}`)}
          >
            {t("steelAssemblyBOM.backToDesignBOM") || "回到設計BOM"}
          </Button>
          <Button
            onClick={() => navigate(`/hrMainPage/addSteelAssemblyBOM/${id}`)}
            variant="default"
          >
            {t("steelAssemblyBOM.importComponent") || "匯入構件"}
          </Button>
        </div>
      </div>

      {/* Filter Section */}
      <div
        className="flex items-center gap-4 px-4 py-3 mb-4"
        style={{ backgroundColor: "#F5F5F5", borderRadius: "8px" }}
      >
        <div className="flex items-center gap-2">
          <span style={{ minWidth: "80px", fontWeight: "500" }}>
            {t("steelAssemblyBOM.guandaId") || "廣達ID"}:
          </span>
          <Input
            placeholder={t("steelAssemblyBOM.searchGuandaId") || "搜尋廣達ID"}
            value={filters.guandaId}
            onChange={(e) => handleFilterChange("guandaId", e.target.value)}
            style={{ width: "150px" }}
          />
        </div>

        <div className="flex items-center gap-2">
          <span style={{ minWidth: "80px", fontWeight: "500" }}>
            {t("steelAssemblyBOM.installationLocation") || "安裝位置"}:
          </span>
          <Input
            placeholder={
              t("steelAssemblyBOM.searchInstallationLocation") || "搜尋安裝位置"
            }
            value={filters.installationLocation}
            onChange={(e) =>
              handleFilterChange("installationLocation", e.target.value)
            }
            style={{ width: "150px" }}
          />
        </div>

        <div className="flex items-center gap-2">
          <span style={{ minWidth: "80px", fontWeight: "500" }}>
            {t("steelAssemblyBOM.sectionSpecification") || "斷面規格"}:
          </span>
          <Input
            placeholder={
              t("steelAssemblyBOM.searchSectionSpec") || "搜尋斷面規格"
            }
            value={filters.metricSizeMark}
            onChange={(e) =>
              handleFilterChange("metricSizeMark", e.target.value)
            }
            style={{ width: "150px" }}
          />
        </div>

        <div className="flex items-center gap-2">
          <span style={{ minWidth: "80px", fontWeight: "500" }}>
            {t("steelAssemblyBOM.transportNumber") || "拆運號碼"}:
          </span>
          <Input
            placeholder={
              t("steelAssemblyBOM.searchTransportNumber") || "搜尋拆運號碼"
            }
            value={filters.transportNumber}
            onChange={(e) =>
              handleFilterChange("transportNumber", e.target.value)
            }
            style={{ width: "150px" }}
          />
        </div>

        <div className="flex gap-2">
          <Button onClick={applyFilters} variant="filled" size="sm">
            {t("steelAssemblyBOM.search") || "搜尋"}
          </Button>
          <Button onClick={clearFilters} variant="default" size="sm">
            {t("steelAssemblyBOM.clear") || "清除"}
          </Button>
        </div>
      </div>

      {/* Column Visibility Section */}
      <div
        className="flex items-center gap-4 px-4 py-3 mb-4"
        style={{ backgroundColor: "#F5F5F5", borderRadius: "8px" }}
      >
        <span style={{ fontWeight: "500", minWidth: "80px" }}>
          {t("steelAssemblyBOM.showColumns") || "顯示欄位"}:
        </span>
        <div className="flex flex-wrap gap-3">
          {baseColumns
            .filter((col) => col.key !== "actions")
            .map((col) => (
              <Checkbox
                key={col.key}
                checked={!hiddenColumns.includes(col.key)}
                onChange={(e) => {
                  const newHiddenColumns = e.target.checked
                    ? hiddenColumns.filter((k) => k !== col.key)
                    : [...hiddenColumns, col.key];
                  setHiddenColumns(newHiddenColumns);
                  localStorage.setItem(
                    COLUMN_VISIBILITY_STORAGE_KEY,
                    JSON.stringify(newHiddenColumns)
                  );
                }}
              >
                {col.title || col.key}
              </Checkbox>
            ))}
        </div>
      </div>

      <div
        className="relative"
        style={
          {
            "--ant-pagination-input-pointer-events": "none",
            "--ant-pagination-input-cursor": "pointer",
            "--ant-pagination-input-caret": "transparent",
          } as React.CSSProperties
        }
      >
        <DndContext
          sensors={sensors}
          modifiers={[restrictToHorizontalAxis]}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={columnOrder}
            strategy={horizontalListSortingStrategy}
          >
            <Table
              rowSelection={rowSelection}
              scroll={{ x: "max-content", y: 600 }}
              columns={columns.map((col) => ({
                ...col,
                onHeaderCell: () => ({ id: col.key }),
              }))}
              dataSource={fileUpload}
              components={{
                header: {
                  cell: DraggableHeaderCell,
                },
              }}
              pagination={{
                current: currentPage,
                pageSize,
                total: filteredData.length,
                onChange: (page, size) => {
                  const nextSize = size || pageSize;
                  setCurrentPage(page);
                  updateURL(undefined, page, nextSize);

                  // Update displayed data for the new page
                  const startIndex = (page - 1) * nextSize;
                  const endIndex = startIndex + nextSize;
                  const paginatedData = filteredData.slice(
                    startIndex,
                    endIndex
                  );
                  setFileUpload(paginatedData);
                },
                onShowSizeChange: (current, size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                  updateURL(undefined, 1, size);

                  // Update displayed data for the new page size
                  const paginatedData = filteredData.slice(0, size);
                  setFileUpload(paginatedData);
                },
                showSizeChanger: true,
                pageSizeOptions: ["10", "25", "50", "100"],
                showQuickJumper: false,
              }}
              rowKey="id"
              bordered
              sticky
            />
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
}

export default DesignBOM;
